import html2canvas from 'html2canvas';
import { getConfigValue } from '../../../util/getConfigValue';


export function constructChartData(selectedDataSet, colorPalette, appState = null) {
  const standardizedData = standardizeChartLabels(selectedDataSet, appState);

  return standardizedData.map((dataset) => {
    const key = colorPalette.getKey(dataset);
    const color = colorPalette.getColorForKey(key);
    return {
      ...dataset,
      minBarLength: 2,
      borderColor: color,
      backgroundColor: color
    };
  });
};

export function standardizeChartLabels(dataset, appState) {
  return dataset.map(item => ({
    ...item,
    label: standardizeChartLabel(item.label, appState)
  }))
}
export function standardizeChartDataLabels(dataset, appState) {
  return dataset.map(ds => ({
    ...ds,
    chartData: {
      dataSets: standardizeChartLabels(ds.chartData.dataSets, appState),
      labels: ds.chartData.labels.map(label => standardizeChartLabel(label, appState)
      )
    }
  }));
}
export function standardizeChartNames(dataset, appState) {
  return dataset.map(item => ({
    ...item,
    name: standardizeChartLabel(item.name, appState)
  }))
}

export function standardizeChartLabel(label, appState) {
  if (label === utilizationReportConstants.allProducts) return getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.AllProducts?.Label, appState)?.Value || "All Products";

  return splitCamelCase(label);
}

export function splitCamelCase(label) {
  return label?.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export function downloadChart(chartRef) {
  const chartInstance = chartRef.current;
  if (chartInstance) {
    const chartCanvas = chartInstance.canvas;
    const imageUrl = chartCanvas.toDataURL("image/jpg");

    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = "chart.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export function downloadDivChart(divId, title) {
  const div = document.getElementById(divId);

  if (!div) {
    console.error("Chart Print area not defined.");
    return;
  }
  const cloneDiv = div.cloneNode(true);

  const sourceCanvases = div.querySelectorAll('canvas');
  const targetCanvases = cloneDiv.querySelectorAll('canvas');

  sourceCanvases.forEach((canvas, index) => {
    const clonedCanvas = targetCanvases[index];
    const ctx = clonedCanvas.getContext('2d');
    ctx.drawImage(canvas, 0, 0);
  })

  const wrapper = document.createElement('div');
  wrapper.style.width = `${div.offsetWidth}px`;
  wrapper.style.position = 'absolute';
  wrapper.style.left = '-9999px';
  wrapper.style.top = '0';

  const titleh3 = document.createElement("h3");
  titleh3.textContent = title;
  titleh3.classList.add("px-5", "color-grey-dl");

  wrapper.appendChild(titleh3);
  wrapper.appendChild(cloneDiv);

  document.body.appendChild(wrapper);

  const scale = 2;

  html2canvas(wrapper, {
    backgroundColor: '#fffff',
    scale: scale,
    useCORS: true,
    allowTaint: true,
    scrollY: -window.scrollY
  }).then((canvas) => {
    const imageUrl = canvas.toDataURL('image/jpg', 0.95);

    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = "chart.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }).catch((error) => {
    console.error("Failed to capture image: ", error);
  }).finally(() => {
    document.body.removeChild(wrapper);
  });
}

export function getTimePeriodTile(timePeriodId, timePeriodTitle, groupingActive = true) {
  switch (timePeriodId) {
    case 6:
    case 7: {
      return groupingActive ? timePeriodTitle : 'Last Year';
    }
    default:
      return timePeriodTitle;
  }
}

export function getTimePeriodLabel(timePeriodId, activeTab = utilizationReportConstants.undefined, groupingActive = true) {

  const isPrior = activeTab === utilizationReportConstants.prior;

  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth(); // 0-indexed (0 = January, 11 = December)
  let day = now.getDate();

  switch (timePeriodId) {
    case 1: // This Month
    case 2: { // Last Month
      let monthOffset = 0;

      if (isPrior) {
        monthOffset = timePeriodId === 1 ? -1 : -2;
      } else {
        monthOffset = timePeriodId === 2 ? -1 : 0;
      }

      const dateForMonth = new Date(year, month + monthOffset);

      return `${dateForMonth.toLocaleString('en-US', { month: 'long' })} ${dateForMonth.getFullYear()}`;
    }
    case 3: // This Quarter      
    case 4: { // Last Quarter
      let qOffset = 0;

      if (isPrior) {
        qOffset = timePeriodId === 3 ? -1 : -2;
      } else {
        qOffset = timePeriodId === 4 ? -1 : 0;
      }

      let currentQuarter = Math.floor(month / 3);
      let adjustedQuarter = currentQuarter + qOffset;
      let qYear = year;

      while (adjustedQuarter < 0) {
        adjustedQuarter += 4;
        qYear--;
      }

      return `Q${adjustedQuarter + 1} ${qYear}`;
    }
    case 5: // Year to Date
      if (isPrior) {
        const end = new Date(year - 1, month, day);
        const endLabel = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        return `YTD Jan 1 - ${endLabel} ${year - 1}`;
      }
      else {
        return `YTD ${year}`;
      }
    case 6: { // Last Year (by Quarter)
      const groupBy = groupingActive ? '(Quarters)' : '';
      return `${year - (isPrior ? 2 : 1)} ${groupBy}`;
    }
    case 7: { // Last Year (by Month)
      const groupBy = groupingActive ? '(Months)' : '';
      return `${year - (isPrior ? 2 : 1)} ${groupBy}`;
    }
    default:
      return 'Invalid Time Period';
  }
};

export function hasValuesGreaterThanZero(dataset) {
  if (!dataset || !Array.isArray(dataset.dataSets)) return false;

  return dataset.dataSets.some(d =>
    Array.isArray(d.totals) && d.totals.reduce((sum, val) => sum + val, 0) > 0
  );
};

export function setChartYAxes(data, setYAxesStepIncrementValue, setMaxYValue) {
  const maxYValue = Math.max(...data.flatMap(item => item.data ?? []));
  let yAxesStepIncrementValue = 0;

  switch (true) {
    case maxYValue <= 5:
      yAxesStepIncrementValue = 1;
      break;
    case maxYValue <= 20:
      yAxesStepIncrementValue = 2;
      break;
    case maxYValue <= 50:
      yAxesStepIncrementValue = 5;
      break;
    case maxYValue <= 100:
      yAxesStepIncrementValue = 10;
      break;
    default:
      yAxesStepIncrementValue = 20;
  }

  setYAxesStepIncrementValue(yAxesStepIncrementValue);
  setMaxYValue((Math.ceil(maxYValue / yAxesStepIncrementValue) * yAxesStepIncrementValue) + yAxesStepIncrementValue);
}

export function setChartXAxes(data, setXAxesStepIncrementValue, setMaxXValue) {
  const maxXValue = Math.max(...data.flatMap(item => item.data ?? []));
  let xAxesStepIncrementValue = 0;

  switch (true) {
    case maxXValue <= 5:
      xAxesStepIncrementValue = 1;
      break;
    case maxXValue <= 20:
      xAxesStepIncrementValue = 2;
      break;
    case maxXValue <= 50:
      xAxesStepIncrementValue = 5;
      break;
    case maxXValue <= 100:
      xAxesStepIncrementValue = 10;
      break;
    default:
      xAxesStepIncrementValue = 20;
  }

  setXAxesStepIncrementValue(xAxesStepIncrementValue);
  setMaxXValue((Math.ceil(maxXValue / xAxesStepIncrementValue) * xAxesStepIncrementValue) + xAxesStepIncrementValue);
}

export const utilizationReportConstants = {
  undefined: "",
  allProducts: "AllProducts",
  number: "number",
  percentage: "percentage",
  prior: "prior",
  current: "current",
  no_data: "No Data"
};

export const baseOptions = {
  defaultFont: "system-ui, 'Segoe UI', Roboto, 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
}

export const baseChartOptions = {
  layout: {
    padding: 0
  },
  legend: {
    position: 'top',
    align: 'end',
    fullSize: false,
    labels: {
      padding: 30,
      font: {
        family: baseOptions.defaultFont,
        size: 20
      }
    }
  },
  datalabels: {
    anchor: 'end',
    align: 'end',
    offset: -1,
    display: true,
    font: {
      family: baseOptions.defaultFont,
      weight: 'bold',
      size: 24
    }
  },
  title: {
    display: true,
    position: 'top',
    align: 'start',
    fullSize: false,

    padding: {
      top: 30
    },
    font: {
      family: baseOptions.defaultFont,
      weight: 'bold',
      size: 24
    }
  },
  tooltip: {
    bodyFont: {
      family: baseOptions.defaultFont,
      size: 20
    },
    titleFont: {
      family: baseOptions.defaultFont,
      size: 24
    }
  },
  scales: {
    x: {
      ticks: {
        beginAtZero: false,
        font: {
          family: baseOptions.defaultFont,
          size: 18
        }
      }
    },
    y: {
      min: 0,
      ticks: {
        beginAtZero: true,
        font: {
          family: baseOptions.defaultFont,
          size: 18
        }
      }
    }
  }
}

export const baseBarChartOptions = {
  ...baseChartOptions
}
export const baseHorizontalBarChartOptions = {
  ...baseChartOptions,
  scales: {
    y: {
      beginAtZero: true,
      ...baseChartOptions.scales.y,    
      ticks: { 
        font: {
          family: baseOptions.defaultFont,
          size: 28,
          weight: 'bold'
        }
      }  
    },
    x: {
      ticks: {
        beginAtZero: true,
        ...baseBarChartOptions.scales.x.ticks
      },
      ...baseBarChartOptions.scales.x
    }
  },
  legend: {
    display: false,
    label: baseBarChartOptions.legend.labels
  },
  title: {
    ...baseBarChartOptions.title, 
    padding: {
      top: 30,
      bottom: 30
    }
  },
  datalabels: baseBarChartOptions.datalabels
}
export const baseLineChartOptions = {
  ...baseChartOptions
}
export const baseDoughnutChartOptions = {
  ...baseChartOptions,
  datalabels: {
    color: '#ffff',
    display: true,
    formatter: function (value, context) {
      return ` ${value} / ${(value / context.dataset.totals[0] * 100).toFixed(0)}%`;
    },
    font: {
      ...baseBarChartOptions.datalabels.font,
      size: 18
    }
  },
  legend: {
    display: false,
    position: 'top',
    align: 'start',
    fullSize: false,
    labels: {
      padding: 5,
      font: baseBarChartOptions.legend.labels.font
    }
  },
  tooltip: {
    ...baseBarChartOptions.tooltip,
    callbacks: {
      label: function (tooltipItem) {
        return ` ${tooltipItem.raw} / ${(tooltipItem.raw / tooltipItem.dataset.totals[0] * 100).toFixed(0)}%`;
      }
    }
  },
  title: {
    ...baseBarChartOptions.title
  }
}

export function beforeDrawHandler(chart) {
	const { ctx, chartArea } = chart;
	if (!chartArea) {
		return;
	}

	const centerConfig = chart.options.plugins.centerTextPlugin;
	const text = centerConfig.text || '';
	const x = (chartArea.left + chartArea.right) / 2;
	const y = (chartArea.top + chartArea.bottom) / 2;

	ctx.save();

	ctx.font = 'bold 16px Arial';
	ctx.fillStyle = '#000';
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';

	ctx.fillText(text, x, y);
	ctx.restore();
}